import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const footerLinks = {
    product: [
      { label: t('nav.dealAnalyzer', 'Deal Analyzer'), path: '/analyzer' },
      { label: t('nav.compFinder', 'Comp Finder'), path: '/comps' },
      { label: 'Repair Estimator', path: '/repair-estimator' },
      { label: 'AI Assistant', path: '/chat' },
      { label: 'Pricing', path: '/pricing', public: true },
    ],
    company: [
      { label: t('footer.about', 'About Us'), path: '/about' },
      { label: t('footer.contact', 'Contact'), path: '/contact' },
      { label: 'Blog', path: '/blog', public: true },
      { label: t('footer.careers', 'Careers'), path: '/careers' },
      { label: t('footer.partners', 'Partners'), path: '/partners' },
    ],
    legal: [
      { label: t('footer.privacy', 'Privacy Policy'), path: '/privacy' },
      { label: t('footer.terms', 'Terms of Service'), path: '/terms' },
      { label: 'Cookie Policy', path: '/cookies' },
      { label: 'Confidentiality', path: '/confidentiality', public: true },
    ],
  };

  const handleProductClick = (e, link) => {
    if (!link.public && !isAuthenticated) {
      e.preventDefault();
      toast.info('Please sign in to access features');
      navigate('/login');
    }
  };

  return (
    <footer className="bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img 
                src="/logo.png" 
                alt="PropertyARV" 
                className="h-24 w-auto brightness-150"
              />
            </Link>
            <p className="text-purple-200 max-w-xs">
              AI-powered real estate investment analysis. Analyze deals, find comps, and estimate repairs in seconds.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.product')}</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.path} 
                    className="text-purple-300 hover:text-pink-300 transition-colors"
                    onClick={(e) => handleProductClick(e, link)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.company')}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="text-purple-300 hover:text-pink-300 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link to={link.path} className="text-purple-300 hover:text-pink-300 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-purple-700/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-purple-400 text-sm">
            © {currentYear} PropertyARV. Powered by Contract 2 Cash LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://www.facebook.com/share/g/1FMEiJuDqC/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-white transition-colors" data-testid="footer-facebook">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://discord.gg/DRxSSTWV" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-white transition-colors" data-testid="footer-discord">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
